import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { useAppContext } from '../../AppContext';
import { subscribeToIncomingChallenges, respondToChallenge } from '../../services/challengeService';
import { useNavigate } from 'react-router-dom'; // Assuming react-router usage, or use handlePageChange

const ChallengeHandler = ({ handlePageChange }) => { // Pass handlePageChange prop if needed
    const { userId, currentUserProfile } = useAppContext();
    const [incomingChallenges, setIncomingChallenges] = useState([]);

    useEffect(() => {
        if (!userId) {
            console.log("ChallengeHandler: No userId, skipping subscription.");
            return;
        }

        console.log("ChallengeHandler: Subscribing to challenges for user:", userId);
        const unsubscribe = subscribeToIncomingChallenges(userId, (challenges) => {
            console.log("ChallengeHandler: Received challenges:", challenges);
            setIncomingChallenges(challenges);
        });

        return () => unsubscribe();
    }, [userId]);

    const handleRespond = async (challengeId, response, gameType) => {
        await respondToChallenge(challengeId, response);
        
        // If accepted, we need to route the user to the game
        if (response === 'accepted') {
            // Logic to open the game modal or navigate to the page
            // For now, we might just trigger a global event or state
            // Ideally, App.js would listen for 'active' challenges too, but simpler:
            if (handlePageChange) {
                handlePageChange('showdown', { challengeId, gameType, mode: 'survival' });
            } else {
                console.log("Challenge Accepted! Navigate to:", gameType);
                // Fallback or event dispatch
                window.dispatchEvent(new CustomEvent('challengeAccepted', { detail: { challengeId, gameType } }));
            }
        }
        
        // Optimistically remove from list
        setIncomingChallenges(prev => prev.filter(c => c.id !== challengeId));
    };

    return (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-full max-w-md px-4 pointer-events-none">
            <AnimatePresence>
                {incomingChallenges.map(challenge => (
                    <motion.div
                        key={challenge.id}
                        initial={{ y: 50, opacity: 0, scale: 0.9 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 20, opacity: 0, scale: 0.9 }}
                        className="mb-2 bg-gray-900/95 backdrop-blur-md border border-cyan-500/50 p-4 rounded-xl shadow-2xl pointer-events-auto flex flex-col gap-3"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center animate-pulse">
                                <LucideIcons.Swords className="text-white w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-white font-bold text-sm">Challenge Incoming!</h4>
                                <p className="text-gray-400 text-xs">
                                    <span className="text-cyan-300 font-bold">{challenge.initiatorName || "A Rival"}</span> wants to battle in <span className="text-purple-300 font-bold capitalize">{challenge.gameType}</span>.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button 
                                onClick={() => handleRespond(challenge.id, 'rejected')}
                                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white text-xs font-bold rounded-lg transition-colors"
                            >
                                Decline
                            </button>
                            <button 
                                onClick={() => handleRespond(challenge.id, 'accepted', challenge.gameType)}
                                className="flex-1 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold rounded-lg transition-all shadow-lg shadow-cyan-500/20"
                            >
                                Accept Duel
                            </button>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};

export default ChallengeHandler;